import { useChatStore } from '@/stores/chat'
import { useFormStore } from '@/stores/form'
import { STEPS } from '@/config/steps'

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function calculateTypingDelay(content: string): number {
  const baseDelay = 500
  const charDelay = 15
  const maxDelay = 2000
  const calculatedDelay = baseDelay + (content.length * charDelay)
  return Math.min(calculatedDelay, maxDelay)
}

export function useChat() {
  const chatStore = useChatStore()
  const formStore = useFormStore()

  async function sendBotMessage(content: string, stepId?: string): Promise<void> {
    chatStore.setTyping(true)
    await delay(calculateTypingDelay(content))
    chatStore.setTyping(false)
    chatStore.addBotMessage(content, stepId)
  }

  function getBotMessage(): string {
    const step = chatStore.currentStep
    if (!step) return ''

    if (typeof step.botMessage === 'function') {
      return step.botMessage(formStore.data as Record<string, any>)
    }
    return step.botMessage
  }

  async function startCurrentStep(): Promise<void> {
    const step = chatStore.currentStep
    if (!step) return

    const message = getBotMessage()
    await sendBotMessage(message, step.id)
  }

  function formatUserResponse(stepId: string, value: any): string {
    const step = STEPS.find(s => s.id === stepId)
    if (!step) return String(value)

    switch (step.inputType) {
      case 'slider':
        return formStore.formattedDebtAmount
      case 'buttons':
        const option = step.options?.find(o => o.value === value)
        return option?.text || String(value)
      case 'name':
        return formStore.fullName
      case 'email':
        return formStore.data.email
      case 'address':
        return `${formStore.data.address}, ${formStore.data.city}, ${formStore.data.state} ${formStore.data.zip}`
      case 'dob':
        return formStore.dob
      case 'phone':
        return formStore.data.phone
      default:
        return String(value)
    }
  }

  async function submitAnswer(value: any): Promise<void> {
    const step = chatStore.currentStep
    if (!step) return

    // Add user response bubble
    const responseText = formatUserResponse(step.id, value)
    chatStore.addUserMessage(responseText, value, step.id)

    // Advance to next step immediately
    chatStore.advanceStep()

    // If there's a next step, show its message
    if (!chatStore.isComplete) {
      await startCurrentStep()
    }
  }

  async function initialize(): Promise<void> {
    // Restore progress if any
    chatStore.restoreProgress()
    formStore.restoreFromStorage()

    // If starting fresh, show first message
    if (chatStore.messages.length === 0) {
      await startCurrentStep()
    }
  }

  return {
    sendBotMessage,
    getBotMessage,
    startCurrentStep,
    submitAnswer,
    initialize,
    formatUserResponse,
  }
}
